import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Chip, Stack, Typography, useMediaQuery } from '@mui/material'
import { AnalyticMetric } from '../../analytics-components/graphs/analytic-metric'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service'
import { ICapitalGood, IFootprintVersion } from '../../../../shared/services/capital-good/capital-good.model'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { factorSourceLabel } from '../../../../shared/constants/product-repository'

function formatDate(value: string | null | undefined): string {
  return value ? moment(value).format('DD/MM/YYYY') : ''
}

function formatFactor(kgCarbonEmission: number, unitAmount: number | null, unitName: string | null | undefined): string {
  const perUnit = unitAmount && unitAmount !== 1 ? `${unitAmount} ` : ''
  return `${kgCarbonEmission.toLocaleString('en-GB', { maximumFractionDigits: 4 })} kgCO2e per ${perUnit}${unitName || 'unit'}`
}

function userName(user: { firstName: string | null, surname: string | null } | null): string {
  if (!user) return ''
  return [user.firstName, user.surname].filter(Boolean).join(' ')
}

/**
 * The product's footprint over time: the live factor first, then every superseded value with its
 * provenance. History records the past only; existing emission calculations always keep the
 * factor they were made with.
 */
export const FootprintHistoryPanel: FunctionComponent<{
  good: ICapitalGood,
}> = ({ good }) => {
  const capitalGoodService = new CapitalGoodService()
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const [versions, setVersions] = useState<IFootprintVersion[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    capitalGoodService.getFootprintVersions(good.id)
      .then((rows) => {
        if (isActive) setVersions(rows)
      })
      .catch((err) => {
        console.error(err)
        if (isActive) setErrorMessage('Unable to load the footprint history. Please try again.')
      })
      .finally(() => {
        if (isActive) setIsLoading(false)
      })
    return () => { isActive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [good.id, good.kgCarbonEmission, good.factorSource])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
        <LoadingSpinner />
      </Box>
    )
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{errorMessage}</Typography>
      </Box>
    )
  }

  const headCells: IHeadCell[] = [
    { id: 'figure', numeric: false, label: 'Superseded figure', sortable: false, isHeading: true },
    { id: 'source', numeric: false, label: 'Source', sortable: false },
    { id: 'gwp', numeric: false, label: 'GWP', sortable: false, includeInMobile: false },
    { id: 'effectiveFrom', numeric: false, label: 'Effective from', sortable: false, includeInMobile: false },
    { id: 'supersededOn', numeric: false, label: 'Superseded on', sortable: false },
    { id: 'by', numeric: false, label: 'By', sortable: false, includeInMobile: false },
    { id: 'replacedBy', numeric: false, label: 'Replaced by', sortable: false },
  ]

  const rows = versions.map((version) => ({
    id: version.id,
    figure: formatFactor(version.kgCarbonEmission, version.unitAmount, version.measurementUnit?.name),
    source: version.sourceOfEmission ? (
      <Stack spacing={0.25}>
        <span>{factorSourceLabel(version.factorSource)}</span>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>{version.sourceOfEmission}</Typography>
      </Stack>
    ) : factorSourceLabel(version.factorSource),
    gwp: version.gwpVersion || '',
    effectiveFrom: formatDate(version.effectiveDate),
    supersededOn: formatDate(version.supersededAt),
    by: userName(version.supersededByUser),
    replacedBy: version.replacedByFactorSource ? factorSourceLabel(version.replacedByFactorSource) : 'Factor removed',
  }))

  const unitName = good.measurementUnit?.name || 'unit'
  const currentUnits = `kgCO2e per ${good.unitAmount && good.unitAmount !== 1 ? `${good.unitAmount} ` : ''}${unitName}`

  return (
    <Stack spacing={2}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        When this product's footprint is replaced, the superseded figure is kept here with its
        provenance. Existing emissions always keep the factor they were calculated with.
      </Typography>

      <Box sx={{ maxWidth: { xs: '100%', md: 420 } }}>
        <AnalyticMetric
          title='Current footprint'
          compact={true}
          value={good.kgCarbonEmission ?? 0}
          units={currentUnits}
          unavailable={good.kgCarbonEmission == null}
          unavailableMessage='No emission factor is recorded against this product.'
          footer={
            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
              <Chip size='small' variant='outlined' color='primary' label={factorSourceLabel(good.factorSource)} />
              {good.gwpVersion && <Chip size='small' variant='outlined' label={good.gwpVersion} />}
              {good.effectiveDate && (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  Effective from {formatDate(good.effectiveDate)}
                </Typography>
              )}
            </Stack>
          }
        />
      </Box>

      {versions.length === 0 ? (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          The footprint has not been replaced since it was first recorded.
        </Typography>
      ) : (
        <TableDisplay
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
        />
      )}
    </Stack>
  )
}
